export type ProviderId = 'jira' | 'trello' | 'standalone';

export interface ProviderCapabilities {
  id: ProviderId;
  label: string;
  // Whether a cross-project "studio overview" / portfolio aggregation exists for this provider.
  supportsStudioOverview: boolean;
  // Whether the provider's dashboard stats endpoint accepts a date range.
  supportsDateRange: boolean;
  // Whether a per-contributor breakdown (Users page) exists for this provider.
  supportsUsers: boolean;
  // Whether a "risky tasks" drill-down table exists for this provider.
  supportsRiskyTasks: boolean;
}

// Adding a future provider: one entry here, one new lightweight stats page (see
// TrelloBoardStatsPage.tsx / StandaloneProjectStatsPage.tsx for the pattern), one branch in
// ProjectDetailPage.tsx. No routing or Projects-provider changes needed.
export const PROVIDER_CAPABILITIES: Record<ProviderId, ProviderCapabilities> = {
  jira: {
    id: 'jira',
    label: 'Jira',
    supportsStudioOverview: true,
    supportsDateRange: true,
    supportsUsers: true,
    supportsRiskyTasks: true,
  },
  trello: {
    id: 'trello',
    label: 'Trello',
    supportsStudioOverview: false,
    supportsDateRange: true,
    supportsUsers: false,
    supportsRiskyTasks: false,
  },
  standalone: {
    id: 'standalone',
    label: 'Standalone',
    supportsStudioOverview: false,
    supportsDateRange: true, // MVPB-110/111: flow-summary now accepts ?from=&to=
    supportsUsers: false,
    supportsRiskyTasks: false,
  },
};

export const getProviderCapabilities = (provider?: string | null): ProviderCapabilities =>
  PROVIDER_CAPABILITIES[(provider as ProviderId) ?? 'jira'] ?? PROVIDER_CAPABILITIES.jira;

// The chat agent specialized for each provider — used to offer "switch to the real agent" once a
// Wizard-built project exists, since WizardAgent's job (propose + build) is done at that point and
// the platform agent is the one that actually knows how to keep managing the project.
export const PROVIDER_AGENT: Record<ProviderId, string> = {
  jira: 'JiraAgentV2',
  trello: 'TrelloAgent',
  standalone: 'StandaloneAgent',
};
